import { useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Botao,
  CartaoIndicador,
  EstadoVazio,
  Selo,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from '../../componentes/ui';
import { useIdentidade } from '../../hooks/useSessao';
import { requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';
import { rotuloDoStatus, tomDoStatus, type ContratoPassivo } from '../PassivoContratos';
import { ParcelasDoContrato } from '../PassivoParcelas';

interface PainelDeParcelas {
  readonly contratosAtivos: number;
  readonly atrasadas: number;
  readonly aVencer: number;
  readonly aguardandoConfirmacao: number;
}

/**
 * Os contratos de Passivo de um cliente — a consulta que decide se a aba existe.
 *
 * Exposta à parte porque o perfil precisa dela **antes** de montar as abas: a
 * aba "ICMS Passivos" só aparece quando a lista não é vazia. O componente da
 * aba usa a mesma chave e recebe o resultado do cache.
 */
export function useContratosPassivo(
  clienteId: string | undefined,
): UseQueryResult<ContratoPassivo[], Error> {
  const identidade = useIdentidade();
  return useQuery({
    queryKey: ['passivo-contratos', clienteId],
    queryFn: () =>
      requisicaoAutenticada<ContratoPassivo[]>(`/passivo/clientes/${clienteId ?? ''}/contratos`),
    // A rota é da operação. Quem não é admin vê os contratos dele em Créditos
    // ICMS Passivos › Contratos, na visão do papel — e o perfil fica sem a aba.
    enabled: clienteId !== undefined && identidade?.isAdmin === true,
  });
}

/**
 * Aba "ICMS Passivos" do perfil do cliente.
 *
 * ## O que ela mostra
 *
 * O painel de parcelas e os contratos de renegociação deste cliente, em
 * qualquer status — inclusive `SIMULACAO`, que é rascunho gravado. A tela de
 * Contratos da divisão mostra a mesma coisa escolhendo o cliente num seletor;
 * aqui o cliente já está escolhido.
 *
 * ## Criar contrato continua na divisão
 *
 * O formulário de criação vive em Contratos (Créditos ICMS Passivos). Não foi
 * duplicado aqui: um contrato de Passivo exige escolher fornecedor e parâmetros
 * que a operação define caso a caso, e dois lugares para criar são dois lugares
 * para divergir. O link leva direto para lá.
 */
export function AbaIcmsPassivo(props: { readonly clienteId: string }): JSX.Element {
  const contratos = useContratosPassivo(props.clienteId);
  const [abrindo, setAbrindo] = useState<ContratoPassivo | null>(null);

  const painel = useQuery({
    queryKey: ['passivo-painel', props.clienteId],
    queryFn: () =>
      requisicaoAutenticada<PainelDeParcelas>(
        `/passivo/clientes/${props.clienteId}/painel-parcelas`,
      ),
  });

  const colunas: readonly ColunaTabela<ContratoPassivo>[] = [
    {
      chave: 'status',
      titulo: 'Status',
      larguraMinima: '9rem',
      celula: (c) => <Selo tom={tomDoStatus(c.status)}>{rotuloDoStatus(c.status)}</Selo>,
    },
    {
      chave: 'valorDividaOriginal',
      titulo: 'Dívida original',
      numerica: true,
      celula: (c) => <ValorFiscal valor={c.valorDividaOriginal} />,
    },
    {
      chave: 'valorFinalPagar',
      titulo: 'Valor a pagar',
      numerica: true,
      celula: (c) => <ValorFiscal valor={c.valorFinalPagar} />,
    },
    {
      chave: 'economia',
      titulo: 'Economia',
      numerica: true,
      celula: (c) => (
        <span className="flex flex-col items-end">
          <ValorFiscal valor={c.economia} variante="economia" />
          <ValorPercentual percentual={c.percentualEconomia} tamanho="pequeno" />
        </span>
      ),
    },
    {
      chave: 'quantidadeParcelas',
      titulo: 'Parcelas',
      numerica: true,
      larguraMinima: '7rem',
      celula: (c) => (
        <span className="valor-fiscal text-sm">
          {c.quantidadeParcelas}
          {!c.cronogramaGerado && (
            <span className="ml-1 text-xs text-conteudo-sutil">(não gerado)</span>
          )}
        </span>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '8rem',
      celula: (c) => (
        <Botao variante="secundario" tamanho="pequeno" onClick={() => setAbrindo(c)}>
          Abrir
        </Botao>
      ),
    },
  ];

  const lista = contratos.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      {painel.data !== undefined && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CartaoIndicador rotulo="Contratos ativos" valor={painel.data.contratosAtivos} />
          <CartaoIndicador
            rotulo="Parcelas atrasadas"
            valor={painel.data.atrasadas}
            {...(painel.data.atrasadas > 0
              ? { detalhe: 'Cobrança em aberto', variante: 'contorno' as const }
              : {})}
          />
          <CartaoIndicador rotulo="A vencer" valor={painel.data.aVencer} />
          <CartaoIndicador
            rotulo="Aguardando confirmação"
            valor={painel.data.aguardandoConfirmacao}
            {...(painel.data.aguardandoConfirmacao > 0
              ? { detalhe: 'O cliente pagou; falta conferir' }
              : {})}
          />
        </div>
      )}

      <section className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold tracking-tight">
              Contratos de renegociação
            </h2>
            <p className="mt-1 text-sm text-conteudo-secundario">
              Dívida de ICMS quitada com crédito comprado. Simulação é rascunho gravado; ativar gera
              as parcelas.
            </p>
          </div>
          <Link
            to="/passivo/contratos"
            className="text-sm text-acento-ciano underline-offset-4 hover:underline"
          >
            Novo contrato na divisão →
          </Link>
        </div>

        {contratos.isError && (
          <ErroDaApi titulo="Não foi possível carregar" erro={contratos.error} />
        )}

        {!contratos.isPending && lista.length === 0 ? (
          <EstadoVazio
            titulo="Nenhum contrato de Passivo"
            descricao="Este cliente não tem renegociação registrada."
          />
        ) : (
          <Tabela
            legenda="Contratos de ICMS Passivo do cliente"
            colunas={colunas}
            dados={lista}
            chaveDaLinha={(c) => c.id}
            chaveDeCabecalhoDaLinha="status"
            carregando={contratos.isPending}
          />
        )}
      </section>

      <ParcelasDoContrato contrato={abrindo} aoFechar={() => setAbrindo(null)} />
    </div>
  );
}
